<template>
  <div class="preview-shell" :class="device">
    <div class="device-frame">
      <div class="device-notch" v-if="device === 'mobile'"></div>
      <iframe ref="frameEl" :src="previewUrl" @load="onFrameLoad" title="معاينة الصفحة الرئيسية"></iframe>
      <div class="preview-loading" v-if="ready !== true">
        <i class="pi" :class="ready === 'error' ? 'pi-exclamation-triangle' : 'pi-spin pi-spinner'"></i>
        <span>{{ ready === 'error' ? 'تعذر الاتصال بالمعاينة — تحقق من إعدادات الموقع' : 'جارٍ تحميل المعاينة...' }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
  device: { type: String, default: 'mobile' },
  sections: { type: Array, default: () => [] },
  selectedKey: { type: String, default: null },
});
const emit = defineEmits(['section-click']);

// Matches the codebase's existing "api_base_url" localStorage-override convention
// (see portal/src/main.js) rather than introducing an unused .env-based config
// scheme — no .env files exist anywhere in this repo today.
const previewOrigin = localStorage.getItem('homepage_preview_origin') || 'https://mobile.moustafahosney.com';
const previewUrl = `${previewOrigin.replace(/\/$/, '')}/__preview/home`;

const frameEl = ref(null);
const ready = ref(false);
let readyTimeout = null;

function clearReadyTimeout() {
  clearTimeout(readyTimeout);
  readyTimeout = null;
}

function sendDraft() {
  if (!ready.value) return;
  frameEl.value?.contentWindow?.postMessage(
    { source: 'homepage-builder', type: 'draft-update', payload: { sections: props.sections } },
    previewOrigin,
  );
}

function sendSelection() {
  if (!ready.value) return;
  frameEl.value?.contentWindow?.postMessage(
    { source: 'homepage-builder', type: 'select-section', payload: { key: props.selectedKey } },
    previewOrigin,
  );
}

function onMessage(event) {
  if (event.origin !== new URL(previewOrigin).origin) return;
  const msg = event.data;
  if (!msg || msg.source !== 'homepage-builder') return;
  if (msg.type === 'preview-ready') {
    ready.value = true;
    clearReadyTimeout();
    sendDraft();
    sendSelection();
  } else if (msg.type === 'section-click') {
    emit('section-click', msg.payload.key);
  }
}

function onFrameLoad() {
  clearReadyTimeout();
  // The preview can announce readiness just before the iframe's load event.
  // Preserve that successful handshake instead of resetting it to loading.
  if (ready.value === true) return;

  ready.value = false;
  // If the preview page never announces itself (wrong origin config, route
  // missing, etc.) surface that instead of leaving a silently blank iframe.
  readyTimeout = setTimeout(() => {
    readyTimeout = null;
    if (ready.value !== true) ready.value = 'error';
  }, 6000);
}

watch(() => props.sections, sendDraft, { deep: true });
watch(() => props.selectedKey, sendSelection);

window.addEventListener('message', onMessage);
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  clearReadyTimeout();
});
</script>

<style scoped>
.preview-shell { display: flex; justify-content: center; }
.device-frame { position: relative; overflow: hidden; background: #fff; }

.preview-shell.mobile .device-frame {
  width: 390px; height: 844px;
  border: 10px solid #111; border-radius: 44px;
  box-shadow: 0 20px 60px rgba(0,0,0,.35);
}
.preview-shell.mobile .device-notch {
  position: absolute; top: 0; left: 50%; transform: translateX(-50%);
  width: 140px; height: 24px; background: #111; border-radius: 0 0 16px 16px; z-index: 2;
}
.preview-shell.desktop .device-frame {
  width: min(1100px, 78vw); height: 720px;
  border: 1px solid var(--border, #e5e7eb); border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.1);
}

.device-frame iframe { width: 100%; height: 100%; border: none; }

.preview-loading {
  position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: .5rem; background: rgba(255,255,255,.9); color: #6b7280; font-size: .85rem;
}
</style>
